import * as z from "zod";

// Gorgias Customer object
// https://developers.gorgias.com/reference/the-customer-object
export const gorgiasCustomerSchema = z.object({
  id: z.number().int(),
  email: z.nullable(z.string()),
  name: z.nullable(z.string()),
  firstname: z.string(),
  lastname: z.string(),
  external_id: z.nullable(z.string()).optional(),
  language: z.nullable(z.string()).optional(),
  note: z.nullable(z.string()).optional(),
  timezone: z.nullable(z.string()).optional(),
  created_datetime: z.nullable(z.string()),
  updated_datetime: z.nullable(z.string()),
});

export type GorgiasCustomer = z.infer<typeof gorgiasCustomerSchema>;

// Gorgias Ticket object
// https://developers.gorgias.com/reference/the-ticket-object
export const gorgiasTicketCustomerSchema = z.object({
  id: z.number().int(),
  email: z.nullable(z.string()).optional(),
  name: z.nullable(z.string()).optional(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
});

export const gorgiasTicketSchema = z.object({
  id: z.number().int(),
  uri: z.string().optional(),
  external_id: z.nullable(z.string()).optional(),
  status: z.string(),
  channel: z.nullable(z.string()),
  via: z.nullable(z.string()).optional(),
  from_agent: z.nullable(z.boolean()).optional(),
  spam: z.boolean().optional(),
  customer: z.nullable(gorgiasTicketCustomerSchema).optional(),
  subject: z.nullable(z.string()),
  language: z.nullable(z.string()).optional(),
  tags: z.array(z.object({ name: z.string() })).optional(),
  messages_count: z.number().int().optional(),
  excerpt: z.nullable(z.string()).optional(),
  created_datetime: z.string(),
  opened_datetime: z.nullable(z.string()).optional(),
  last_received_message_datetime: z.nullable(z.string()).optional(),
  last_message_datetime: z.nullable(z.string()).optional(),
  updated_datetime: z.nullable(z.string()).optional(),
  closed_datetime: z.nullable(z.string()).optional(),
  trashed_datetime: z.nullable(z.string()).optional(),
  snooze_datetime: z.nullable(z.string()).optional(),
  is_unread: z.boolean().optional(),
  meta: z.nullable(z.record(z.string(), z.unknown())).optional(),
});

export type GorgiasTicket = z.infer<typeof gorgiasTicketSchema>;

// Gorgias TicketMessage object
// https://developers.gorgias.com/reference/the-ticketmessage-object
const gorgiasMessageSenderSchema = z.object({
  id: z.number().int().optional(),
  email: z.nullable(z.string()).optional(),
  name: z.nullable(z.string()).optional(),
});

export const gorgiasMessageSchema = z.object({
  id: z.number().int(),
  uri: z.string().optional(),
  message_id: z.nullable(z.string()).optional(),
  ticket_id: z.number().int().optional(),
  external_id: z.nullable(z.string()).optional(),
  public: z.boolean().optional(),
  channel: z.nullable(z.string()).optional(),
  via: z.nullable(z.string()).optional(),
  sender: z.nullable(gorgiasMessageSenderSchema).optional(),
  integration_id: z.nullable(z.number().int()).optional(),
  from_agent: z.boolean(),
  subject: z.nullable(z.string()).optional(),
  body_text: z.nullable(z.string()),
  body_html: z.nullable(z.string()).optional(),
  stripped_text: z.nullable(z.string()).optional(),
  stripped_html: z.nullable(z.string()).optional(),
  created_datetime: z.string(),
  sent_datetime: z.nullable(z.string()).optional(),
  opened_datetime: z.nullable(z.string()).optional(),
  deleted_datetime: z.nullable(z.string()).optional(),
  failed_datetime: z.nullable(z.string()).optional(),
});

export type GorgiasMessage = z.infer<typeof gorgiasMessageSchema>;

// Gorgias paginated list wrapper; next page is meta.next_cursor (null on the last page).
export const gorgiasListResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    meta: z
      .object({ next_cursor: z.nullable(z.string()).optional() })
      .optional(),
  });

// Gorgias renders `{{…}}` form templates before POSTing; an unresolved template
// arrives as the literal `{{…}}` string or "", both meaning absent.
const rendered = (raw: unknown): string | undefined =>
  typeof raw === "string" && raw.length > 0 && !raw.startsWith("{{")
    ? raw
    : undefined;

export const GORGIAS_REDO_SUPPORT_TICKET_ID_META_KEY = "redo_support_ticket_id";
// Unused and stamped empty: registered webhook forms still reference it, and a missing key drops delivery.
export const GORGIAS_REDO_MARKETING_CONVERSATION_ID_META_KEY =
  "redo_marketing_conversation_id";
// The support-v2 channel thread the handoff came from. Stamped so an agent's
// reply relays onto the exact thread the customer is texting, rather than
// being guessed from the ticket — a support topic can own several threads.
export const GORGIAS_REDO_CHANNEL_THREAD_ID_META_KEY = "redo_channel_thread_id";

// Every message the handoff seeds into the external ticket gets an external_id
// with this prefix; the resolution backfill keys off it to skip our own seeded
// history instead of re-importing it. Shared by the producer and the importer.
export const REDO_SEEDED_EXTERNAL_ID_PREFIX = "redo-";

// Stamped on every ticket the handoff creates. `external_id` and `meta` carry
// the Redo ids but neither is a rule-builder condition, so a tag is the only
// field a merchant can route on: "when ticket created, if tags contain Redo
// Escalation, assign to a human". Gorgias creates the tag on first use.
export const GORGIAS_REDO_ESCALATION_TAG = "Redo Escalation";

/**
 * Scopes the public app asks for at consent. Escalation only *needs* the
 * ticket/customer read+write pairs — see `REQUIRED_ESCALATION_SCOPES`, which
 * gates whether a connection can escalate at all — but a tagged ticket also
 * needs `tags:write`, so we request it. It is deliberately not required:
 * grants issued before it was added carry every other scope, and demanding it
 * would flip those merchants from "escalates untagged" to "does not escalate".
 *
 * Both OAuth entry points (support escalation and chargebacks) authorize the
 * SAME app into the SAME per-team GorgiasIntegration — last writer wins on the
 * stored token — so a narrower list in either path silently strips the other's
 * access. Import this; do not re-declare it.
 */
export const GORGIAS_OAUTH_SCOPES = [
  "openid",
  "email",
  "profile",
  "offline",
  "integrations:read",
  "integrations:write",
  "macros:read",
  "tickets:read",
  "tickets:write",
  "customers:read",
  "customers:write",
  "tags:write",
] as const;

// Ticket-status webhook payload, normalized on ingest.
export const gorgiasTicketStatusWebhookSchema = z.object({
  ticketId: z.preprocess(rendered, z.string()),
  status: z.preprocess(rendered, z.string()),
  snoozeDatetime: z.preprocess(rendered, z.string().optional()),
  redoSupportTicketId: z.preprocess(rendered, z.string().optional()),
  // Ignored; older registered forms still send it.
  redoMarketingConversationId: z.preprocess(rendered, z.string().optional()),
});

export type GorgiasTicketStatusWebhook = z.infer<
  typeof gorgiasTicketStatusWebhookSchema
>;

// Ticket-message webhook payload: an agent reply on an escalation ticket.
export const gorgiasTicketMessageWebhookSchema = z.object({
  ticketId: z.preprocess(rendered, z.string()),
  messageId: z.preprocess(rendered, z.string()),
  fromAgent: z.preprocess(rendered, z.string().optional()),
  channel: z.preprocess(rendered, z.string().optional()),
  // "False" on internal notes. Some accounts report an internal note with
  // channel "email", so channel alone can't tell what the customer sees.
  // Absent on integrations registered before this field was added.
  public: z.preprocess(rendered, z.string().optional()),
  externalId: z.preprocess(rendered, z.string().optional()),
  bodyText: z.preprocess(rendered, z.string().optional()),
  // The channel thread the escalation was raised from — what a relayed reply is sent on.
  redoChannelThreadId: z.preprocess(rendered, z.string().optional()),
  // Ignored; older registered forms still send it.
  redoMarketingConversationId: z.preprocess(rendered, z.string().optional()),
});

export type GorgiasTicketMessageWebhook = z.infer<
  typeof gorgiasTicketMessageWebhookSchema
>;

// Synthetic customer address on escalation tickets that Redo can relay replies
// for: agents reply "via email", the ticket-message webhook carries it to Redo,
// SES discards the mail.
export const GORGIAS_REPLY_ADDRESS_DOMAIN = "helpdesk-reply.getredomail.com";

export function gorgiasReplyAddress(customerPhone: string): string {
  return `${customerPhone.replace(/\D/g, "")}@${GORGIAS_REPLY_ADDRESS_DOMAIN}`;
}

interface GorgiasMessageParty {
  id?: number;
  email?: string;
  name?: string;
}

interface GorgiasMessageSource {
  type: string;
  from?: { name?: string; address: string };
  to?: { name?: string; address: string }[];
}

export interface GorgiasCreateMessage {
  channel: string;
  via?: string;
  from_agent: boolean;
  body_text: string;
  sender?: GorgiasMessageParty;
  receiver?: GorgiasMessageParty;
  source?: GorgiasMessageSource;
  /** "redo-" prefix marks seeded history so the resolution backfill skips it. */
  external_id?: string;
  /** When the message was sent. */
  sent_datetime?: string;
  /** When the message was created, which is what their UI displays */
  created_datetime?: string;
}

export interface GorgiasCreateTicketRequest {
  customer?: { id: number };
  channel?: string;
  via?: string;
  subject?: string;
  status?: "open" | "closed";
  /** When the ticket was created. */
  created_datetime?: string;
  external_id?: string;
  meta?: Record<string, unknown>;
  /**
   * Tags to apply on creation. Needs `tags:write` on top of `tickets:write` —
   * Gorgias 403s the whole create without it, so callers must be able to retry
   * untagged (see `createSmsTicket`). Requested via {@link GORGIAS_OAUTH_SCOPES}.
   */
  tags?: { name: string }[];
  messages: GorgiasCreateMessage[];
}

// Ticket summary used in dispute detail UI and evidence generation
export const gorgiasTicketSummarySchema = z.object({
  id: z.number().int(),
  subject: z.string(),
  status: z.string(),
  createdAt: z.string(),
  snippet: z.string(),
  url: z.string(),
});

export type GorgiasTicketSummary = z.infer<typeof gorgiasTicketSummarySchema>;
